/**
 * APP_REGISTRY — Single source of truth for all navigable Plerity apps.
 *
 * Both the shell AppSwitcher (dashboard-shell) and the SidebarNav AppSwitcher
 * (dashboard-shared) consume this registry instead of maintaining separate lists.
 *
 * @package @plerity/dashboard-shared
 */

/** Unified app entry used by all navigation components. */
export interface AppEntry {
  /** Unique app identifier */
  id: string;
  /** Display label */
  label: string;
  /** Full production URL */
  url: string;
  /** App category — determines which navigation views show it */
  category: "shell" | "ssr" | "utility";
  /** Whether this is the currently active app (set at runtime) */
  active?: boolean;
}

/** Complete registry of all navigable apps (19 entries: 18 shells + 1 ssr). */
export const APP_REGISTRY: AppEntry[] = [
  // ── Core platform (SSR) ────────────────────────────────────────────────────
  { id: "portal", label: "Portal", url: "https://app.animasmarketing.com", category: "ssr" },

  // ── Communications ─────────────────────────────────────────────────────────
  // comms merged into ops — comms.animasmarketing.com is retired
  { id: "chat", label: "Chat", url: "https://chat.animasmarketing.com", category: "shell" },

  // ── Marketing & Sales ──────────────────────────────────────────────────────
  // crm consolidated into the ops shell (2026-05-26) — links directly to the
  // ops-hosted CRM home. The legacy crm.animasmarketing.com 301 is removed in
  // Phase 8.1, so do not rely on it here.
  { id: "crm", label: "CRM", url: "https://ops.animasmarketing.com/crm", category: "shell" },
  { id: "seo", label: "SEO", url: "https://seo.animasmarketing.com", category: "shell" },
  { id: "social", label: "Social", url: "https://social.animasmarketing.com", category: "shell" },
  { id: "email", label: "Email", url: "https://email.animasmarketing.com", category: "shell" },
  { id: "reviews", label: "Reviews", url: "https://reviews.animasmarketing.com", category: "shell" },
  { id: "calls", label: "Calls", url: "https://calls.animasmarketing.com", category: "shell" },

  // ── Finance & Ops ──────────────────────────────────────────────────────────
  { id: "finance", label: "Finance", url: "https://finance.animasmarketing.com", category: "shell" },
  { id: "operations", label: "Operations", url: "https://ops.animasmarketing.com", category: "shell" },
  { id: "booking", label: "Booking", url: "https://booking.animasmarketing.com", category: "shell" },
  { id: "reports", label: "Reports", url: "https://reports.animasmarketing.com", category: "shell" },

  // ── Platform & Dev ─────────────────────────────────────────────────────────
  { id: "ai", label: "AI", url: "https://ai.animasmarketing.com", category: "shell" },
  { id: "platform", label: "Platform", url: "https://platform.animasmarketing.com", category: "shell" },
  { id: "design", label: "Design", url: "https://design.animasmarketing.com", category: "shell" },

  // ── Research & Data ────────────────────────────────────────────────────────
  { id: "research", label: "Research", url: "https://research.animasmarketing.com", category: "shell" },
  { id: "data", label: "Data", url: "https://data.animasmarketing.com", category: "shell" },
  { id: "health", label: "Health", url: "https://health.animasmarketing.com", category: "shell" },

  // ── Operator / Governance ──────────────────────────────────────────────────
  // Added 2026-06-03 (deployment-gap close after Phase 1 plan flip was lost in
  // commit 1c66afae129). Plan: agents/plans/governance_web_app_plan_2026-05-24.md
  { id: "governance", label: "Governance", url: "https://gov.animasmarketing.com", category: "shell" },

  // ── Utilities ──────────────────────────────────────────────────────────────
  // Builder app removed from navigation (2026-03-27) — website editing is integrated
  // in the portal's site-editor. See agents/plans/ for builder evaluation.
  // Launcher app removed (2026-05-01) — see agents/analysis/frontend_apps_organization_analysis_2026-05-01.md
];

/** Shell-category apps for the dashboard-shell AppSwitcher. */
export function getShellApps(): AppEntry[] {
  return APP_REGISTRY.filter((app) => app.category === "shell");
}

/** SSR apps for the SidebarNav AppSwitcher (Portal only — Builder removed). */
export function getSsrApps(): AppEntry[] {
  return APP_REGISTRY.filter((app) => app.category === "ssr");
}

/** Utility apps for the shell AppSwitcher utility links section. */
export function getUtilityApps(): AppEntry[] {
  return APP_REGISTRY.filter((app) => app.category === "utility");
}
